/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { memoize, range } from 'lodash';
import type { WMJSDimension, Dimension, WMLayer } from '@opengeoweb/webmap';
import { WMInvalidDateValues, getCorrectWMSDimName } from '@opengeoweb/webmap';
import { MAX_NUMBER_STEPS_IN_TIME_SLIDER_TO_PREFETCH } from './constants';
import type { Timespan } from '../types/Timespan';

/**
 *
 * @param wmTimeDim WMDimension time containing dimension values
 * @param stepsToCheck Array with timestamps as posix timestamps to check
 * @param isAnimating If the application is running an animation loop
 * @returns A string list with ISO8601 values to fetch
 */
export const makeDimValuesToPrefetch = (
  wmTimeDim: WMJSDimension,
  stepsToCheck: number[],
  isAnimating: boolean,
): string[] => {
  if (wmTimeDim) {
    if (stepsToCheck?.length > 0) {
      const timePrefetchValuesSet = new Set<number>([]);
      stepsToCheck.forEach((t) => {
        if (!t) {
          return;
        }
        const closestValue = wmTimeDim.getClosestValueForTime(t);
        if (!WMInvalidDateValues.has(closestValue)) {
          timePrefetchValuesSet.add(new Date(closestValue).getTime());
        }
      });
      const currentTimeOfLayer = wmTimeDim.currentValue
        ? new Date(wmTimeDim.currentValue).getTime()
        : stepsToCheck[0];
      // Sort it according to the current time value
      const timePrefetchValuesArray = Array.from(timePrefetchValuesSet);

      const futurePrefetchValues = timePrefetchValuesArray.filter(
        (timeStepAsTime) => timeStepAsTime >= currentTimeOfLayer,
      );
      const pastPrefetchValues = timePrefetchValuesArray.filter(
        (timeStepAsTime) => timeStepAsTime < currentTimeOfLayer,
      );

      // eslint-disable-next-line @typescript-eslint/require-array-sort-compare
      futurePrefetchValues.sort();
      isAnimating
        ? // eslint-disable-next-line @typescript-eslint/require-array-sort-compare
          pastPrefetchValues.sort()
        : // eslint-disable-next-line @typescript-eslint/require-array-sort-compare
          pastPrefetchValues.sort().reverse();

      const dimensionValuesToPrefetchCombined = [
        ...futurePrefetchValues,
        ...pastPrefetchValues,
      ];

      const dimValuesToPrefetch = dimensionValuesToPrefetchCombined.map(
        (timeStamp) => wmTimeDim.getClosestValueForTime(timeStamp),
      );

      return dimValuesToPrefetch;
    }
  }
  return [];
};

export const extendPrefetchListWithModelRunTimeSteps = (
  wmLayer: WMLayer,
  stepsToCheck: number[],
): number[] => {
  if (stepsToCheck.length > 1) {
    const wmRefTimeDim = wmLayer.getDimension('reference_time');
    const wmTimeDim = wmLayer.getDimension('time');
    if (wmRefTimeDim && wmTimeDim) {
      // eslint-disable-next-line @typescript-eslint/require-array-sort-compare
      const sortedPrefetchList = stepsToCheck.sort();
      const startPrefetchTime = sortedPrefetchList[0];
      const nextPrefetchTime = sortedPrefetchList[1];
      const endPrefetchTime = sortedPrefetchList[sortedPrefetchList.length - 1];

      const stepSizeMs =
        sortedPrefetchList.length > 1
          ? nextPrefetchTime - startPrefetchTime
          : 3600;
      const first = wmTimeDim.getValueForIndexAsTime(0);
      const last = wmTimeDim.getValueForIndexAsTime(wmTimeDim.size() - 1);
      const newStartPrefetchTime =
        startPrefetchTime -
        Math.round((startPrefetchTime - first) / stepSizeMs) * stepSizeMs;
      const newEndPrefetchTime =
        endPrefetchTime -
        Math.round((endPrefetchTime - last) / stepSizeMs) * stepSizeMs;
      return range(newStartPrefetchTime, newEndPrefetchTime, stepSizeMs);
    }
  }
  return stepsToCheck;
};

/**
 * We use the WMLayer dimension logic to obtain the dimension value to use in the WMS GetMap request. The input is the dimensions array which is typically the properties set for the layer component.
 * The input time is descretized to the closest value which is available in the WMS layer.
 *
 * @param dimensions Array of dimension key/values to use as input
 * @param wmLayer The layer for which we would like to do the request
 * @returns A map which returns a key/value to use
 */
export const getDimensionParamsForWMS = (
  dimensions: Dimension[],
  wmLayer: WMLayer | null,
): Record<string, string> | undefined => {
  // Check if pre-requisites are OK. Possibly still fetching WMS GetCapabilities.
  if (!wmLayer?.isConfigured) {
    return undefined;
  }

  const dimProps: Record<string, string> = {};
  const wmLayerDims = wmLayer.getDimensions();

  // Loop without forEach so we can exit and return.
  for (const wmLayerDim of wmLayerDims) {
    // Check if passed as part of the list of dimensions
    const layerDim = dimensions.find((dim) => dim.name === wmLayerDim.name);

    if (layerDim) {
      const closestValue = wmLayerDim.getClosestValue(layerDim.currentValue);
      if (!WMInvalidDateValues.has(closestValue)) {
        wmLayerDim.setValue(closestValue);
        dimProps[getCorrectWMSDimName(wmLayerDim.name!)] = closestValue;
      } else {
        // The requested value is not in the range of the dimension, image should not show
        return undefined;
      }
    } else {
      // Not specified so fall back to current/default value from WMLayer
      dimProps[getCorrectWMSDimName(wmLayerDim.name!)] = wmLayerDim.currentValue
        ? wmLayerDim.currentValue
        : wmLayerDim.defaultValue;
    }
  }

  return dimProps;
};

/**
 * Calculates number of timesteps for given timespan
 * @param timespan Object of Timespan, with start/stop/end in milliseconds
 * @returns Timestamps array in milliseconds
 */
const timeSpanToTimeList = (timespan: Timespan): number[] => {
  if (timespan?.start && timespan.end && timespan.step) {
    if (timespan.step <= 0 || timespan.start > timespan.end + timespan.step) {
      return [];
    }
    const numStepsToFetch =
      (timespan.end + timespan.step - timespan.start) / timespan.step;

    if (numStepsToFetch < 1) {
      return [];
    }

    if (numStepsToFetch > MAX_NUMBER_STEPS_IN_TIME_SLIDER_TO_PREFETCH) {
      const msg = `too many steps to prefetch: ${numStepsToFetch}`;
      console.warn(msg);
      return [];
    }

    const stepsToCheck = range(
      timespan.start,
      timespan.end + timespan.step,
      timespan.step,
    );
    // Find out which dimension time values are available for prefetching
    if (stepsToCheck.length > MAX_NUMBER_STEPS_IN_TIME_SLIDER_TO_PREFETCH) {
      const msg = `too many steps to prefetch: ${stepsToCheck.length}`;
      console.warn(msg);
      return [];
    }
    return stepsToCheck;
  }
  return [];
};

/**
 * Calculates number of timesteps for given timespan
 * @param timespan Object of Timespan, with start/stop/end in milliseconds
 * @returns Timestams array in milliseconds
 */
export const makeTimeList = memoize(timeSpanToTimeList);
